import { useState } from "react";
import { FiPlus, FiLock, FiEyeOff, FiFileText } from "react-icons/fi";
import phoneFrame from "../../assets/home-page/phone-frame.png";
import { useInView } from "../../hooks/useInView";

const FAQ_ITEMS = [
  {
    question: "What types of loans does Genz Paisa offer?",
    answer:
      "We offer personal loans for travel, emergencies, and everyday purchases, with flexible repayment options tailored to your salary income.",
  },
  {
    question: "How do I apply for a loan?",
    answer:
      "Simply fill out the application form with your basic details, PAN, and monthly salary — our digital process takes just a few minutes.",
  },
  {
    question: "How do I know if I'm eligible?",
    answer:
      "You can check your eligibility instantly using our loan calculator without affecting your credit score.",
  },
  {
    question: "What documents do I need?",
    answer:
      "You'll typically need your PAN card, a valid ID proof, and recent salary slips or bank statements.",
  },
  {
    question: "How long does the application process take?",
    answer:
      "Most applications are reviewed and approved within minutes thanks to our fully automated verification system.",
  },
  // {
  //   question: "How long does the application process take?",
  //   answer:
  //     "Most applications are reviewed and approved within minutes thanks to our fully automated verification system.",
  // },
];

const SECURITY_FEATURES = [
  {
    icon: FiLock,
    title: "Secure & Protected",
    description:
      "Your details are safeguarded using 256-bit bank-grade encryption protocols.",
  },
  {
    icon: FiEyeOff,
    title: "Privacy First",
    description:
      "Your personal information is strictly private and never shared with third parties.",
  },
  {
    icon: FiFileText,
    title: "Transparent & Clear",
    description:
      "No hidden fees or unexpected costs. All policies clearly outlined.",
  },
];

function FaqAccordionItem({
  question,
  answer,
  isOpen,
  onToggle,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="rounded-2xl border border-solid border-[#e2e8f0]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="text-sm font-bold text-brand-purple sm:text-base">
          {question}
        </span>
        <FiPlus
          className={`h-4 w-4 shrink-0 text-brand-purple transition-transform ${
            isOpen ? "rotate-45" : ""
          }`}
        />
      </button>
      {isOpen ? (
        <p className="px-6 pb-5 text-sm leading-5 text-[#475569]">{answer}</p>
      ) : null}
    </div>
  );
}

export function FaqSecurity() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { ref, inView } = useInView<HTMLDivElement>();

  return (
    <section id="faq" className="mx-auto w-full max-w-[1280px] px-4 pb-12 pt-8 sm:pb-16 sm:pt-10">
      <div
        ref={ref}
        className={`grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16 ${inView ? "in-view" : ""}`}
      >
        {/* FAQ column */}
        <div>
          <span className="reveal-scroll delay-1 text-xs font-semibold uppercase tracking-[0.6px] text-brand-teal">
            Frequently Asked Questions
          </span>
          <h2 className="reveal-scroll delay-2 mt-2 text-3xl font-extrabold leading-tight text-brand-purple">
            It's easier than ever before.
          </h2>

          <div className="mt-6 flex flex-col gap-3">
            {FAQ_ITEMS.map((item, i) => (
              <FaqAccordionItem
                key={`${item.question}-${i}`}
                question={item.question}
                answer={item.answer}
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
              />
            ))}
          </div>
        </div>

        {/* Security column */}
        <div>
          <span className="reveal-scroll delay-1 text-xs font-semibold uppercase tracking-[0.6px] text-brand-teal">
            Your Data. Your Privacy. Your Trust.
          </span>
          <h2 className="reveal-scroll delay-2 mt-2 text-3xl font-extrabold leading-tight text-brand-purple">
            Your information is secure.
          </h2>
          <p className="mt-3 text-base leading-6 text-[#475569]">
            We take the security of your personal and financial information
            seriously. Your data is encrypted and handled strictly in
            accordance with regulatory best practices.
          </p>

          <div className="mt-8 flex flex-col gap-8 sm:flex-row sm:items-center">
            {/* Phone mockup */}
            <img
              src={phoneFrame}
              alt="genz paisa app screen confirming your information is secure, RBI regulated NBFC partner"
              className="w-[230px] shrink-0"
            />

            <ul className="flex flex-1 flex-col gap-5">
              {SECURITY_FEATURES.map((feature) => (
                <li key={feature.title} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#e6f4f2]">
                    <feature.icon className="h-4 w-4 text-brand-teal" />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-brand-purple">
                      {feature.title}
                    </p>
                    <p className="text-xs leading-5 text-[#475569]">
                      {feature.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
